import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ContextualTour from '@/components/feature/ContextualTour';
import { supabase } from '@/lib/supabase';
import { useJanelaDeAlerta } from '@/lib/notificacoes';

interface Treinamento {
  id: string;
  nome: string;
  dataConclusao: string;
  vigenciaDias: number;
  obrigatorio: boolean;
  descricao: string;
}

interface FuncionarioRecord {
  id: string;
  condominio_id: string;
  nome: string;
  telefone?: string;
  email?: string;
  cpf?: string;
  rg?: string;
  data_nascimento?: string;
  data_admissao?: string;
  ultimo_exame?: string;
  funcao?: string;
  profissoes_ids?: string[];
  atividades?: string[];
  treinamentos?: Treinamento[];
}

const funcionariosTourSteps = [
  {
    title: 'Cadastro de Funcionários',
    description:
      'Gerencie todos os colaboradores do condomínio. Cada funcionário tem ficha completa com dados pessoais, profissionais, atividades extras e treinamentos.',
    icon: 'ri-team-line',
  },
  {
    title: 'Adicionar novo funcionário',
    description:
      'Clique em "Novo Funcionário" para cadastrar um colaborador. Preencha nome, CPF, função, data de admissão e dados de contato.',
    icon: 'ri-user-add-line',
  },
  {
    title: 'Ficha completa do funcionário',
    description:
      'Clique no ícone de engrenagem para abrir a ficha individual. Lá você encontra dados pessoais, profissionais, atividades extras e a gestão de treinamentos.',
    icon: 'ri-profile-line',
  },
  {
    title: 'Gestão de Treinamentos',
    description:
      'Dentro da ficha do funcionário, a seção de treinamentos mostra o status de cada certificação: vigente (verde), a vencer em até 30 dias (amarelo) ou vencido (vermelho).',
    icon: 'ri-award-line',
  },
  {
    title: 'Painel geral de treinamentos',
    description:
      'Acesse "Treinamentos" no menu lateral para ver todos os treinamentos de todos os funcionários em um só lugar, com filtros por status e alertas de vencimento.',
    icon: 'ri-bar-chart-line',
  },
];

function obterSaudeTreinamentos(
  treinamentos: Treinamento[],
  janelaDias: number,
): {
  vencidos: number;
  aVencer: number;
  vigentes: number;
  piorStatus: 'ok' | 'atencao' | 'critico';
} {
  if (!treinamentos || treinamentos.length === 0)
    return { vencidos: 0, aVencer: 0, vigentes: 0, piorStatus: 'ok' };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  let vencidos = 0,
    aVencer = 0,
    vigentes = 0;
  treinamentos.forEach((t) => {
    const d = new Date(t.dataConclusao);
    d.setDate(d.getDate() + t.vigenciaDias);
    const diff = Math.ceil((d.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) vencidos++;
    else if (diff <= janelaDias) aVencer++;
    else vigentes++;
  });
  return {
    vencidos,
    aVencer,
    vigentes,
    piorStatus: vencidos > 0 ? 'critico' : aVencer > 0 ? 'atencao' : 'ok',
  };
}

export default function FuncionariosTab() {
  const { id: condoId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const janelaTreinamento = useJanelaDeAlerta('treinamento');
  const [funcionarios, setFuncionarios] = useState<FuncionarioRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FuncionarioRecord | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [creating, setCreating] = useState(false);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const formatData = (iso?: string) => {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const fetchFuncionarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .from('funcionarios')
        .select('*')
        .eq('condominio_id', condoId)
        .order('nome', { ascending: true });
      if (fErr) setError(fErr.message);
      else setFuncionarios((data || []) as FuncionarioRecord[]);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    if (condoId) void fetchFuncionarios();
  }, [condoId, fetchFuncionarios]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: dErr } = await supabase.from('funcionarios').delete().eq('id', deleteTarget.id);
    if (dErr) {
      showToast(`Erro: ${dErr.message}`, 'error');
      setDeleteTarget(null);
      return;
    }
    setFuncionarios((prev) => prev.filter((f) => f.id !== deleteTarget.id));
    showToast(`${deleteTarget.nome} excluído com sucesso.`);
    setDeleteTarget(null);
  };

  const handleNovoFuncionario = async () => {
    setCreating(true);
    const { data, error: insErr } = await supabase
      .from('funcionarios')
      .insert({ condominio_id: condoId, nome: 'Novo Funcionário' })
      .select()
      .single();

    if (insErr) {
      showToast(`Erro ao criar: ${insErr.message}`, 'error');
      setCreating(false);
      return;
    }

    if (data) {
      navigate(`/condominios/${condoId}/funcionarios/${data.id}`);
    }
    setCreating(false);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  if (error)
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          onClick={fetchFuncionarios}
          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          {funcionarios.length} funcionário(s) cadastrado(s)
        </p>
        <button
          onClick={handleNovoFuncionario}
          disabled={creating}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {creating ? (
            <>
              <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
              Criando...
            </>
          ) : (
            <>
              <i className="ri-add-line"></i>Novo Funcionário
            </>
          )}
        </button>
      </div>

      {funcionarios.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-team-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Nenhum funcionário cadastrado</p>
          <p className="text-sm text-foreground-500 mt-1">
            Clique em "Novo Funcionário" para começar.
          </p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Funcionário
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Função
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                    Telefone
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                    Admissão
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                    Último Exame
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Treinamentos
                  </th>
                  <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {funcionarios.map((func) => (
                  <tr key={func.id} className="hover:bg-background-100/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-primary-600 text-sm"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground-800 truncate">
                            {func.nome}
                          </p>
                          <p className="text-xs text-foreground-500 truncate">
                            {func.email || '-'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border bg-secondary-100 text-secondary-900 border-secondary-200">
                        {func.funcao || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-sm text-foreground-700">{func.telefone || '-'}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-foreground-600">
                        {formatData(func.data_admissao)}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-foreground-600">
                        {formatData(func.ultimo_exame)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const treinos = func.treinamentos;
                        if (!treinos || treinos.length === 0)
                          return <span className="text-xs text-foreground-400">—</span>;
                        const saude = obterSaudeTreinamentos(treinos, janelaTreinamento);
                        return (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <span
                                className={`w-2 h-2 rounded-full ${saude.vencidos > 0 ? 'bg-red-500' : 'bg-green-500'}`}
                              ></span>
                              <span
                                className={`text-xs font-medium ${saude.piorStatus === 'critico' ? 'text-red-600' : saude.piorStatus === 'atencao' ? 'text-amber-600' : 'text-green-600'}`}
                              >
                                {saude.piorStatus === 'critico'
                                  ? `${saude.vencidos} venc.`
                                  : saude.piorStatus === 'atencao'
                                    ? `${saude.aVencer} próx.`
                                    : `${saude.vigentes} ok`}
                              </span>
                            </div>
                            <span className="text-xs text-foreground-400">/ {treinos.length}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/condominios/${condoId}/funcionarios/${func.id}`}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                          title="Editar funcionário"
                        >
                          <i className="ri-settings-3-line text-sm"></i>
                        </Link>
                        <button
                          onClick={() => setDeleteTarget(func)}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir o funcionário <strong>{deleteTarget.nome}</strong>?
              Todos os dados serão perdidos.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : 'ri-close-circle-line text-red-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}

      <ContextualTour
        storageKey="tour_funcionarios_treinamentos"
        steps={funcionariosTourSteps}
        title="Funcionários e Treinamentos"
      />
    </div>
  );
}
